import { useRef, useState } from "react";
import { serverTimestamp } from "firebase/firestore";
// Custom Components
import Card from "../card/Card";
import VerticalFormInputField from "../form-input-field/VerticalFormInputField.jsx";
// Utils
import { useLanguage } from "../../utils/language.js";
import {
  canUpdateNumberField,
  isStringNullOrEmpty,
  validateMobile,
  validateNumber,
} from "../../utils/string_util.js";
import {
  checkIfPhoneNumberExist,
  saveRSVP,
} from "../../utils/firebase_util.js";
// Styles
import styles from "./rsvp.module.css";
import LoadingModal from "../loading/LoadingModal.jsx";
import HorizontalFormInputField from "../form-input-field/HorizontalFormInputField.jsx";
import {
  NOTIFICATION_TYPE,
  useNotification,
} from "../../utils/notification_context.js";
import { useModal } from "../../utils/modal-context.js";

function RSVP() {
  const { getTranslation } = useLanguage();
  const { showNotification, showErrorNotification } = useNotification();
  const { showModal } = useModal();

  const defaultFormState = {
    rsvp: null,
    guestName: "",
    guestContact: "",
    invitedBy: null,
    partnerAttending: null,
    partnerName: "",
    childrenAttending: null,
    childrenCount: 0,
    babyChairCount: 0,
    isVegetarian: null,
    dietaryRestrictions: "",
    notFirstSubmit: false,
  };
  const [formState, setFormState] = useState(defaultFormState);
  const [isLoading, setIsLoading] = useState(false);

  const rsvpRef = useRef(null);
  const guestNameRef = useRef(null);
  const guestContactRef = useRef(null);
  const invitedByRef = useRef(null);
  const partnerAttendingRef = useRef(null);
  const partnerNameRef = useRef(null);
  const childrenAttendingRef = useRef(null);
  const childrenCountRef = useRef(null);
  const childrenBabyChairRef = useRef(null);
  const vegetarianRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, type } = e.target;

    if (type === "tel") {
      // Allow only numbers and the '+' sign
      const sanitizedValue = value.replace(/[^0-9]/g, "");
      setFormState((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
      return;
    }

    if (type === "number" && !canUpdateNumberField(value)) {
      return;
    } else if (type === "radio") {
      if (name === "invitedBy") {
        setFormState((prev) => {
          if (
            (value === "bride" && prev[name] === "bride") ||
            (value === "groom" && prev[name] === "groom")
          ) {
            return prev;
          }
          return {
            ...prev,
            [name]: value,
          };
        });
      } else {
        setFormState((prev) => {
          if (
            (value === "Yes" && prev[name] === true) ||
            (value === "No" && prev[name] === false)
          ) {
            return prev;
          }
          return {
            ...prev,
            [name]: value === "Yes" ? true : false,
          };
        });
      }
    } else {
      setFormState((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleRSVP = (res) => {
    setFormState((prev) => {
      // return { ...prev, rsvp: res };
      if (prev.rsvp === res) {
        return prev;
      }

      const necessaryData = {
        guestName: prev.guestName,
        guestContact: prev.guestContact,
        invitedBy: prev.invitedBy,
        notFirstSubmit: prev.notFirstSubmit,
      };

      return {
        ...defaultFormState,
        ...necessaryData,
        rsvp: res,
      };
    });
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      let confirm = window.confirm(
        getTranslation("do_you_confirm_your_choice")
      );
      if (confirm) {
        setIsLoading(true);

        const phoneExists = await checkIfPhoneNumberExist(
          formState.guestContact
        );

        if (!phoneExists) {
          const data = {
            ...formState,
            childrenCount: parseInt(formState.childrenCount, 10),
            babyChairCount: parseInt(formState.babyChairCount),
            id: getIdFromName(formState.guestName),
            createdAt: serverTimestamp(),
          };

          try {
            await saveRSVP(data);
            showModal(
              getTranslation("form_success_title"),
              getTranslation("form_success_msg_submitted"),
              getTranslation("modal_close_button")
            );
            clearForm();
          } catch (e) {
            console.error("Error saving RSVP: ", e);
          } finally {
            setIsLoading(false);
          }
        } else {
          showErrorNotification(getTranslation("form_error_msg_already_exist"));
          setIsLoading(false);
        }
      }
    }
  };

  const validateForm = () => {
    if (formState.rsvp === null) {
      showPrefixedErrorNotif(getTranslation("form_error_msg_rsvp_choice"));
      requestFocusNView(rsvpRef);
      return false;
    }

    if (isStringNullOrEmpty(formState.guestName)) {
      showPrefixedErrorNotif(getTranslation("form_error_msg_guest_name"));
      requestFocusNView(guestNameRef);
      return false;
    }

    if (!validateMobile(formState.guestContact)) {
      showPrefixedErrorNotif(getTranslation("form_error_msg_guest_contact"));
      requestFocusNView(guestContactRef);
      return false;
    }

    if (formState.invitedBy === null) {
      showPrefixedErrorNotif(getTranslation("form_error_msg_invited_by"));
      requestFocusNView(invitedByRef);
      return false;
    }

    if (formState.rsvp === true) {
      if (formState.partnerAttending === null) {
        showPrefixedErrorNotif(
          getTranslation("form_error_msg_partner_attendance")
        );
        requestFocusNView(partnerAttendingRef);
        return false;
      }

      if (formState.partnerAttending) {
        if (isStringNullOrEmpty(formState.partnerName)) {
          showPrefixedErrorNotif(getTranslation("form_error_msg_partner_name"));
          requestFocusNView(partnerNameRef);
          return false;
        }
      }

      if (formState.invitedBy === "groom") {
        if (formState.childrenAttending === null) {
          showPrefixedErrorNotif(
            getTranslation("form_error_msg_children_attendance")
          );
          requestFocusNView(childrenAttendingRef);
          return false;
        }

        if (formState.childrenAttending) {
          if (
            formState.childrenCount < 1 ||
            !validateNumber(formState.childrenCount)
          ) {
            showPrefixedErrorNotif(
              getTranslation("form_error_msg_children_count")
            );
            requestFocusNView(childrenCountRef);
            return false;
          }

          if (!validateNumber(formState.babyChairCount)) {
            showPrefixedErrorNotif(
              getTranslation("form_error_msg_children_baby_chair_count")
            );
            requestFocusNView(childrenBabyChairRef);
            return false;
          }
        }
      }

      if (formState.isVegetarian === null) {
        showPrefixedErrorNotif(
          getTranslation("form_error_msg_vegetarian_choice")
        );
        requestFocusNView(vegetarianRef);
        return false;
      }
    }

    return true;
  };

  const showPrefixedErrorNotif = (errorItem) => {
    showErrorNotification(
      `${getTranslation("form_error_msg_title")} ${errorItem}`
    );
  };

  const clearForm = () => setFormState(defaultFormState);

  const requestFocusNView = (currentRef) => {
    currentRef.current.focus();
    currentRef.current.scrollIntoView();
  };

  const getIdFromName = (name) => {
    return name.toLowerCase().replace(" ", "-");
  };

  return (
    <section className={styles.rsvpContainer}>
      <h2 className="sectionTitle">{getTranslation("rsvp")}</h2>

      <div className={styles.rsvpContentContainer} ref={rsvpRef} tabIndex="-1">
        <Card
          className={`${styles.rsvpButton} ${
            formState.rsvp === null && "bouncing-vertical"
          }`}
          style={{
            backgroundColor: formState.rsvp === true ? "#931b1c" : "white",
            color: formState.rsvp === true ? "white" : "black",
          }}
          onClick={() => handleRSVP(true)}
        >
          {getTranslation("rsvp_yes_attend")}
        </Card>

        <Card
          className={styles.rsvpButton}
          style={{
            backgroundColor: formState.rsvp === false ? "#931b1c" : "white",
            color: formState.rsvp === false ? "white" : "black",
          }}
          onClick={() => handleRSVP(false)}
        >
          {getTranslation("rsvp_no_dont_attend")}
        </Card>
      </div>

      <p className={styles.rsvpMessage}>
        {formState.rsvp === null
          ? getTranslation("rsvp_desc")
          : formState.rsvp === true
          ? getTranslation("rsvp_yes_desc")
          : getTranslation("rsvp_no_desc")}
      </p>

      <section className={styles.rsvpForm}>
        {/* Guest Details */}
        <div className={styles.formSectionCard + " boxShadow"}>
          <VerticalFormInputField
            title={getTranslation("rsvp_guest_name_title")}
            hint="eg: Aiven Lee Ah Biao"
            inputName="guestName"
            value={formState.guestName}
            onChange={handleChange}
            ref={guestNameRef}
          />
          <VerticalFormInputField
            title={getTranslation("rsvp_guest_contact_title")}
            hint="eg: 0123456789"
            inputName="guestContact"
            inputType="tel"
            pattern="^(?:\+?60|0)1[0-46-9]-?[0-9]{7,8}$"
            value={formState.guestContact}
            onChange={handleChange}
            ref={guestContactRef}
          />
        </div>

        {/* Invited By */}
        {formState.rsvp !== null && (
          <div className={styles.formSectionCard + " boxShadow"}>
            <div className={styles.checkboxRow}>
              <label
                className={styles.checkboxRowTitle}
                dangerouslySetInnerHTML={{
                  __html: getTranslation("rsvp_guest_invited_by_title"),
                }}
              />
              <div
                className={styles.checkboxChoiceContainer}
                ref={invitedByRef}
                tabIndex="-1"
              >
                <div className="flex flex-row items-center lg:mb-2">
                  <input
                    id="invited_by_groom"
                    type="radio"
                    name="invitedBy"
                    value="groom"
                    checked={formState.invitedBy === "groom"}
                    onChange={handleChange}
                    className={styles.checkbox}
                  />
                  <label htmlFor="invited_by_groom" className="mr-0.5">
                    {getTranslation("groom")}
                  </label>
                </div>

                <div className="flex flex-row">
                  <label htmlFor="invited_by_bride" className="mr-0.5">
                    <input
                      id="invited_by_bride"
                      type="radio"
                      name="invitedBy"
                      value="bride"
                      checked={formState.invitedBy === "bride"}
                      onChange={handleChange}
                      className={styles.checkbox}
                    />
                    {getTranslation("bride")}
                    {/* <img src="assets/icons/bride.webp" height={18} width={18} /> */}
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Partner Details */}
        {formState.rsvp && (
          <div className={styles.formSectionCard + " boxShadow"}>
            <div className={styles.checkboxRow}>
              <label
                className={styles.checkboxRowTitle}
                dangerouslySetInnerHTML={{
                  __html: getTranslation("rsvp_guest_partner_attending_title"),
                }}
              />
              <div
                className={styles.checkboxChoiceContainer}
                ref={partnerAttendingRef}
                tabIndex="-1"
              >
                <div>
                  <label htmlFor="partner_attending_yes">
                    <input
                      id="partner_attending_yes"
                      type="radio"
                      name="partnerAttending"
                      value="Yes"
                      checked={formState.partnerAttending === true}
                      onChange={handleChange}
                      className={styles.checkbox}
                    />
                    {getTranslation("btn_yes")}
                  </label>
                </div>

                <div>
                  <input
                    id="partner_attending_no"
                    type="radio"
                    name="partnerAttending"
                    value="No"
                    checked={formState.partnerAttending === false}
                    onChange={handleChange}
                    className={styles.checkbox}
                  />
                  <label htmlFor="partner_attending_no">
                    {getTranslation("btn_no")}
                  </label>
                </div>
              </div>
            </div>

            {formState.partnerAttending && (
              <div style={{ marginTop: "1rem" }}>
                <VerticalFormInputField
                  title={getTranslation("rsvp_guest_partner_name_title")}
                  hint={getTranslation("leave_blank_if_na")}
                  inputName="partnerName"
                  value={formState.partnerName}
                  onChange={handleChange}
                  inputClassName={styles.formInputFieldBackground}
                  ref={partnerNameRef}
                />
              </div>
            )}
          </div>
        )}

        {/* Children Details */}
        {formState.rsvp && formState.invitedBy === "groom" && (
          <div className={styles.formSectionCard + " boxShadow"}>
            <div className={styles.checkboxRow}>
              <label
                className={styles.checkboxRowTitle}
                dangerouslySetInnerHTML={{
                  __html: getTranslation("rsvp_guest_children_attending_title"),
                }}
              />
              <div
                className={styles.checkboxChoiceContainer}
                ref={childrenAttendingRef}
                tabIndex="-1"
              >
                <div>
                  <input
                    id="children_attending_yes"
                    type="radio"
                    name="childrenAttending"
                    value="Yes"
                    checked={formState.childrenAttending === true}
                    onChange={handleChange}
                    className={styles.checkbox}
                  />
                  <label htmlFor="children_attending_yes">
                    {getTranslation("btn_yes")}
                  </label>
                </div>
                <div>
                  <input
                    id="children_attending_no"
                    type="radio"
                    name="childrenAttending"
                    value="No"
                    checked={formState.childrenAttending === false}
                    onChange={handleChange}
                    className={styles.checkbox}
                  />
                  <label htmlFor="children_attending_no">
                    {getTranslation("btn_no")}
                  </label>
                </div>
              </div>
            </div>

            {formState.childrenAttending && (
              <div style={{ marginTop: "1rem" }}>
                <HorizontalFormInputField
                  title={getTranslation("rsvp_guest_how_many_children_title")}
                  hint={getTranslation("leave_blank_if_na")}
                  inputType="number"
                  max="5"
                  inputName="childrenCount"
                  value={formState.childrenCount}
                  onChange={handleChange}
                  ref={childrenCountRef}
                />
                <HorizontalFormInputField
                  title={getTranslation("rsvp_guest_baby_chair_needed_title")}
                  hint={getTranslation("leave_blank_if_na")}
                  inputType="number"
                  inputName="babyChairCount"
                  value={formState.babyChairCount}
                  onChange={handleChange}
                  ref={childrenBabyChairRef}
                />
              </div>
            )}
          </div>
        )}

        {/* Dietary Preferences */}
        {formState.rsvp && (
          <div className={styles.formSectionCard + " boxShadow"}>
            <div
              className={styles.checkboxRow}
              style={{ marginBottom: "1rem" }}
            >
              <label
                className={styles.checkboxRowTitle}
                dangerouslySetInnerHTML={{
                  __html: getTranslation("rsvp_guest_vegetarian_title"),
                }}
              />
              <div
                className={styles.checkboxChoiceContainer}
                ref={vegetarianRef}
                tabIndex="-1"
              >
                <div>
                  <input
                    id="is_vegetarian_yes"
                    type="radio"
                    name="isVegetarian"
                    value="Yes"
                    checked={formState.isVegetarian === true}
                    onChange={handleChange}
                    className={styles.checkbox}
                  />
                  <label htmlFor="is_vegetarian_yes">
                    {getTranslation("btn_yes")}
                  </label>
                </div>

                <div>
                  <input
                    id="is_vegetarian_no"
                    type="radio"
                    name="isVegetarian"
                    value="No"
                    checked={formState.isVegetarian === false}
                    onChange={handleChange}
                    className={styles.checkbox}
                  />
                  <label htmlFor="is_vegetarian_no">
                    {getTranslation("btn_no_2")}
                  </label>
                </div>
              </div>
            </div>

            <p className={styles.isVegeNotes}>
              <strong className={styles.boldTitle}>
                {getTranslation("rsvp_guest_vegetarian_note_title")}
              </strong>
              {getTranslation("rsvp_guest_vegetarian_note")}
            </p>

            <VerticalFormInputField
              title={getTranslation("rsvp_guest_other_dietary_restrictions")}
              hint={getTranslation("leave_blank_if_na")}
              inputName="dietaryRestrictions"
              value={formState.dietaryRestrictions}
              onChange={handleChange}
              style={{ marginTop: "1rem" }}
            />
          </div>
        )}

        {/* 
        {formState.rsvp && (
          <AttendNumber
            attendNumber={attendNumber}
            setAttendNumber={setAttendNumber}
            className={styles.attendNumberContainer}
          />
        )} */}

        <Card
          className={`${styles.rsvpButton} ${styles.submitBtn}`}
          backgroundColor="#931b1c"
          onClick={(e) => submitHandler(e)}
        >
          <p className={styles.submitBtnTxt}>{getTranslation("rsvp_submit")}</p>
        </Card>
      </section>

      <LoadingModal isLoading={isLoading} />
    </section>
  );
}

export default RSVP;
